/* =============================================================================
   Amstar — Hair Quiz logic
   Vanilla JS. State machine + scoring + Klaviyo subscribe + result reveal.
   Reads Klaviyo public key + list id from data attributes on .hq.
   ============================================================================= */
(function () {
  'use strict';

  const root = document.querySelector('.hq');
  if (!root) return;

  // ---- Config from data attributes ----
  const KLAVIYO_PUBLIC_KEY = root.dataset.klaviyoKey || '';
  const KLAVIYO_LIST_ID = root.dataset.klaviyoList || '';
  const SCREEN_COUNT = 6; // 6 questions, used for progress calculation

  // ---- State ----
  const state = {
    current: 'intro',
    answers: [], // array of selected option indices (one per question)
    email: ''
  };

  // ---- Scoring matrix ----
  // Each question's answers map to weighted scores across 4 personas.
  // Index of option (0-3) -> { frizz, glass, bombshell, waves }
  const SCORING = [
    // Q1 — Your hair, day to day
    [
      { bombshell: 2, glass: 1 },           // Fine and falls flat
      { bombshell: 2, frizz: 1 },           // Thick and hard to dry
      { waves: 3 },                          // Wavy or curly
      { glass: 3 }                           // Already straight
    ],
    // Q2 — Biggest hair frustration
    [
      { frizz: 3 },                          // Frizz the second I step outside
      { glass: 3 },                          // Looks dull
      { frizz: 1, glass: 1, waves: 1, bombshell: 1 }, // Heat damage (neutral)
      { waves: 3 }                           // Don't know how to style waves
    ],
    // Q3 — Hair length
    [
      { bombshell: 2, glass: 1 },           // Short / pixie
      { bombshell: 1, glass: 1, waves: 1 }, // Just past shoulders
      { glass: 2, waves: 1 },                // Mid-back or longer
      { waves: 2 }                           // About texture
    ],
    // Q4 — Go-to look
    [
      { glass: 3 },                          // Stick-straight
      { bombshell: 3 },                      // Bouncy blowout
      { waves: 3 },                          // Defined waves or curls
      { frizz: 3 }                           // Smooth, anti-frizz
    ],
    // Q5 — Time you have
    [
      { bombshell: 2 },                      // Under 5 min
      { bombshell: 1, glass: 1 },           // 5 to 10 min
      { waves: 2, glass: 1 },                // 10 to 20 min
      { glass: 1, waves: 1 }                 // Style the night before
    ],
    // Q6 — What can we help with
    [
      { frizz: 1, glass: 1, bombshell: 1, waves: 1 }, // Right tool
      { frizz: 1, glass: 1, bombshell: 1, waves: 1 }, // Routine
      { waves: 1, bombshell: 1 },            // Styling techniques
      { frizz: 1, glass: 1, bombshell: 1, waves: 1 }  // All of the above
    ]
  ];

  // ---- Persona keys ordered by tie-breaking preference ----
  const PERSONA_ORDER = ['bombshell', 'glass', 'frizz', 'waves'];

  function calculateResult() {
    const totals = { frizz: 0, glass: 0, bombshell: 0, waves: 0 };
    state.answers.forEach((optionIdx, qIdx) => {
      const scores = SCORING[qIdx] && SCORING[qIdx][optionIdx];
      if (!scores) return;
      Object.keys(scores).forEach(p => { totals[p] += scores[p]; });
    });
    // Find max; tie-break by PERSONA_ORDER
    let max = -1;
    let winner = 'bombshell';
    PERSONA_ORDER.forEach(p => {
      if (totals[p] > max) {
        max = totals[p];
        winner = p;
      }
    });
    return winner;
  }

  // ---- Screen navigation ----
  function show(screenName) {
    const screens = root.querySelectorAll('.hq__screen');
    screens.forEach(s => s.setAttribute('data-active', s.dataset.screen === screenName ? 'true' : 'false'));
    state.current = screenName;
    // Restore the previously chosen answer when revisiting a question (e.g. via Back)
    if (screenName.charAt(0) === 'q') {
      const qi = parseInt(screenName.slice(1), 10) - 1;
      const scr = root.querySelector('.hq__screen[data-screen="' + screenName + '"]');
      if (scr) {
        scr.querySelectorAll('.hq-option').forEach(function (o) {
          if (parseInt(o.dataset.option, 10) === state.answers[qi]) o.setAttribute('data-selected', 'true');
          else o.removeAttribute('data-selected');
        });
      }
    }
    updateProgress();
    // Scroll to top of quiz container on transition
    const top = root.getBoundingClientRect().top + window.scrollY;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: top - 16, behavior: reduceMotion ? 'auto' : 'smooth' });
    // Focus first interactive element for keyboard / a11y
    setTimeout(() => {
      const active = root.querySelector('.hq__screen[data-active="true"]');
      if (!active) return;
      const focusable = active.querySelector('button, input, a[href]');
      if (focusable) focusable.focus({ preventScroll: true });
    }, reduceMotion ? 0 : 250);
  }

  function updateProgress() {
    const fill = root.querySelector('.hq__progress-fill');
    if (!fill) return;
    let pct = 0;
    if (state.current.startsWith('q')) {
      const n = parseInt(state.current.slice(1), 10);
      pct = (n / SCREEN_COUNT) * 100;
    } else if (state.current === 'email') {
      pct = 100;
    } else if (state.current === 'result') {
      pct = 100;
    }
    fill.style.width = pct + '%';
  }

  // ---- Option selection (auto-advance) ----
  function handleOptionClick(e) {
    const btn = e.target.closest('.hq-option');
    if (!btn) return;
    const screen = btn.closest('.hq__screen');
    if (!screen) return;
    const screenName = screen.dataset.screen;
    if (!screenName.startsWith('q')) return;
    const qIdx = parseInt(screenName.slice(1), 10) - 1;
    const optionIdx = parseInt(btn.dataset.option, 10);
    // Persist answer
    state.answers[qIdx] = optionIdx;
    // Visual feedback
    btn.setAttribute('data-selected', 'true');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Advance after brief feedback
    setTimeout(() => {
      btn.removeAttribute('data-selected');
      const nextQ = qIdx + 2; // qIdx is 0-based; next question number is qIdx+2 (1-based)
      if (nextQ > SCREEN_COUNT) {
        show('email');
      } else {
        show('q' + nextQ);
      }
    }, reduceMotion ? 0 : 280);
  }

  // ---- Back button ----
  function handleBackClick(e) {
    const btn = e.target.closest('.hq__back');
    if (!btn) return;
    e.preventDefault();
    if (state.current === 'intro') return;
    if (state.current === 'email') {
      show('q' + SCREEN_COUNT);
      return;
    }
    if (state.current === 'result') {
      show('email');
      return;
    }
    if (state.current.startsWith('q')) {
      const n = parseInt(state.current.slice(1), 10);
      if (n === 1) {
        show('intro');
      } else {
        show('q' + (n - 1));
      }
    }
  }

  // ---- Intro start ----
  function handleStartClick(e) {
    const btn = e.target.closest('[data-hq-start]');
    if (!btn) return;
    show('q1');
  }

  // ---- Retake ----
  function handleRetakeClick(e) {
    const btn = e.target.closest('[data-hq-retake]');
    if (!btn) return;
    state.answers = [];
    state.email = '';
    show('intro');
  }

  // ---- Email submission + Klaviyo ----
  function handleEmailSubmit(e) {
    const form = e.target.closest('[data-hq-email-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('.hq-email__input');
    const errBox = form.querySelector('.hq-email__error');
    const cta = form.querySelector('.hq__cta');
    const email = input.value.trim();
    // Simple validation
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errBox.textContent = 'Please enter a valid email address.';
      errBox.setAttribute('data-visible', 'true');
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }
    errBox.removeAttribute('data-visible');
    input.removeAttribute('aria-invalid');
    state.email = email;
    // Fire the Meta Lead event BEFORE the Klaviyo call. Klaviyo is best-effort
    // and we must not make the conversion signal depend on a 3rd-party hiccup.
    trackMetaLead();
    cta.disabled = true;
    cta.dataset.originalLabel = cta.textContent;
    cta.innerHTML = 'Submitting<span aria-hidden="true">...</span>';
    // POST to Klaviyo (best-effort; show result regardless if Klaviyo errors,
    // since we don't want to gate the experience on a 3rd-party hiccup)
    subscribeToKlaviyo(email)
      .catch(err => { console.warn('Klaviyo subscribe failed:', err); })
      .finally(() => {
        cta.disabled = false;
        cta.textContent = cta.dataset.originalLabel || 'See My Results';
        revealResult();
      });
  }

  // ---- Meta Pixel: Lead on quiz completion ----
  // WHY: the Amstar dataset (1212998134291314) fires PageView / ViewContent /
  // AddToCart / InitiateCheckout / AddPaymentInfo / Purchase / Search and
  // nothing else. Quiz completions were therefore invisible to Meta, which
  // meant quiz ads could only run in a Purchase-optimised ad set - optimising
  // toward a conversion this page cannot produce. This event makes the quiz a
  // measurable, optimisable objective with a real cost-per-lead.
  //
  // No value is sent: a lead is not revenue, and passing a fabricated value
  // would corrupt ROAS reporting across the account.
  function trackMetaLead() {
    try {
      if (typeof window.fbq !== 'function') return;
      window.fbq('track', 'Lead', {
        content_name: 'Hair Routine Quiz',
        content_category: calculateResult()
      });
    } catch (err) {
      console.warn('Meta Lead event failed:', err);
    }
  }

  function subscribeToKlaviyo(email) {
    if (!KLAVIYO_PUBLIC_KEY || !KLAVIYO_LIST_ID) {
      console.warn('Klaviyo not configured. Skipping subscribe.');
      return Promise.resolve();
    }
    const persona = calculateResult();
    const url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_PUBLIC_KEY);
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Hair Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  hair_quiz_persona: persona,
                  hair_quiz_completed_at: new Date().toISOString()
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } }
        }
      }
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify(body)
    }).then(r => {
      if (!r.ok && r.status !== 202) throw new Error('Klaviyo HTTP ' + r.status);
    });
  }

  function revealResult() {
    const persona = calculateResult();
    // Toggle persona blocks
    const blocks = root.querySelectorAll('.hq-result[data-persona]');
    blocks.forEach(b => {
      b.style.display = (b.dataset.persona === persona) ? 'block' : 'none';
    });
    show('result');
  }

  // ---- Event wiring ----
  root.addEventListener('click', e => {
    handleOptionClick(e);
    handleBackClick(e);
    handleStartClick(e);
    handleRetakeClick(e);
  });
  root.addEventListener('submit', handleEmailSubmit);

  // ---- Init ----
  show('intro');
})();
